import { ErrorBoundary } from '@components/shared/ErrorBoundary'
import { POSTHOG_API_KEY, POSTHOG_HOST } from '@constant/featureFlags'
import { createBeforeSend } from '@fordeer/posthog-error-tracking'
import { FordeerApp } from '@fordeer/ui-components'
import StoreProvider from '@providers/StoreProvider'
import { AppProvider } from '@shopify/polaris'
import '@shopify/polaris/build/esm/styles.css'
import en from '@shopify/polaris/locales/en.json'
import Link from '@utils/link'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import type React from 'react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter } from 'react-router-dom'
import './bootstrap/clearLocalStorageBeforeApp'
import { installMockAppBridgeElements } from '@lib/mockAppBridgeElements'
import AppRoutes from './routes/AppRoutes'

if (import.meta.env.VITE_MOCK_BRIDGE_ENABLED) {
  installMockAppBridgeElements()
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('Root element not found')

const posthogBeforeSend = createBeforeSend({
  appName: FordeerApp.Invoice,
  environment: import.meta.env.MODE,
})

const posthogBeforeSendCompat = posthogBeforeSend as unknown as NonNullable<
  Parameters<typeof posthog.init>[1]
>['before_send']

if (POSTHOG_API_KEY) {
  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
    disable_compression: true,
    // posthog-js 1.379+ silently drops all capture() calls for likely-bot UAs unless opted out
    opt_out_useragent_filter: import.meta.env.DEV,
    // posthog-js 1.379+ defaults to marking localhost as internal/test users
    internal_or_test_user_hostname: import.meta.env.DEV ? null : undefined,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    before_send: posthogBeforeSendCompat,
  })
}

const queryClient = new QueryClient()

const Root = (): React.ReactNode => {
  return (
    <StrictMode>
      <ErrorBoundary>
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <StoreProvider>
              <BrowserRouter>
                <AppProvider i18n={en} linkComponent={Link}>
                  <AppRoutes />
                </AppProvider>
              </BrowserRouter>
            </StoreProvider>
          </QueryClientProvider>
        </PostHogProvider>
      </ErrorBoundary>
    </StrictMode>
  )
}

ReactDOM.createRoot(rootEl).render(<Root />)
